import './GroceryFeedItemList.scss'
import '../GroceryFeed/GroceryStockSettingsDialog.scss'
import '@ica-azure/ica-elements/IcaIconInfo'
import { useEffect, useState, useMemo } from 'react'
import EditIcon from '../Icons/EditIcon'
import { stockText, getRunningOutThreshold, getRunningOutThresholdTooltip } from '../../utils/stockUtils'
import ArrowIconMinimal from '../Icons/ArrowIconMinimal'
import { dateStringToRestockDisplayDate } from '../../utils/dateUtils'
import TabletextInput from '../TableTextInput/TableTextInput'
import InputError from '../InputError/InputError'
import ResetIcon from '../Icons/ResetIcon'
import StoreGroceryStock from '../../types/StoreGroceryStock'
import { displayPrice } from '../../utils/displayPrice'
import ItemImage from '../ItemImage/ItemImage'
import Tag from '../Tag/Tag'
import { getTagTypeForStock } from '../Picklist/picklistHelpers'
import { IcaDialog } from '../IcaElements/IcaDialog'
import { IcaAttentionHeader } from '../IcaElements/IcaAttentionHeader'
import { IcaButton } from '../IcaElements/IcaButton'
import { IcaHeader } from '../IcaElements/IcaHeader'
import { IcaCardRow } from '../IcaElements/IcaCardRow'
import { IcaMediaObject } from '../IcaElements/IcaMediaObject'
import { IcaMeta } from '../IcaElements/IcaMeta'
import { IcaSegment } from '../IcaElements/IcaSegment'
import { IcaRadio } from '../IcaElements/IcaRadio'
import { IcaInputNumberStepper } from '../IcaElements/IcaInputNumberStepper'
import { IcaButtonIcon } from '../IcaElements/IcaButtonIcon'
import { IcaTooltip } from '../IcaElements/IcaTooltip'
import { SortDirection } from '../../utils/sortUtils'

export interface IGroceryFeedItemList {
  groceries:  StoreGroceryStock[]
  sortProps?: GroceryFeedItemListSortProp[]
  saveCustomPrice(grocery: StoreGroceryStock, price: number | undefined): Promise<void>
  deleteCustomPrice(grocery: StoreGroceryStock): Promise<void>
  deleteGrocery: (grocery: StoreGroceryStock | undefined) => Promise<void>
  onChangeStockSettings?: (item: StoreGroceryStock, threshold?: number, maxQuantity?: number) => Promise<void>
}

export interface GroceryFeedItemListSortProp {
  name: string
  sortValue?: (item: StoreGroceryStock) => string | number
}

interface GroceryFeedItemListSortOption extends GroceryFeedItemListSortProp {
  direction: SortDirection
  value: string | undefined
}

export default function GroceryFeedItemList({
  groceries,
  sortProps,
  saveCustomPrice,
  deleteCustomPrice,
  deleteGrocery,
  onChangeStockSettings
}: IGroceryFeedItemList) {
  const [sortedGroceries, setSortedGroceries] = useState<StoreGroceryStock[]>([...groceries])
  const [sortOptions, setSortOptions] = useState<GroceryFeedItemListSortOption[] | undefined>(undefined)
  const [currentSortOption, setCurrentSortOption] = useState<GroceryFeedItemListSortOption | undefined>(undefined)
  const [selectedGrocery, setSelectedGrocery] = useState<StoreGroceryStock>()
  const [customPriceError, setCustomPriceError] = useState<string | null>(null)
  const [deletingGrocery, setDeletingGrocery] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [thresholdMode, setThresholdMode] = useState<'default' | 'custom'>('default')
  const [thresholdValue, setThresholdValue] = useState<number | null>(null)
  const [thresholdInputError, setThresholdInputError] = useState<string | null>(null)
  const [maxQuantityMode, setMaxQuantityMode] = useState<'default' | 'custom'>('default')
  const [maxQuantity, setMaxQuantity] = useState<number | null>(selectedGrocery?.maxQuantity ?? null)
  const [maxQuantityInputError, setMaxQuantityInputError] = useState<string | null>(null)

  useEffect(() => {
    if (sortProps !== undefined) {
      const newSortOptions: GroceryFeedItemListSortOption[] = 
        [{ name: 'Sorteringsalternativ', direction: 'asc', value: undefined }]
      
      const options: GroceryFeedItemListSortOption[] = sortProps
        .flatMap(prop => {
          return [
            { ...prop, name: `${prop.name} - Stigande`, direction: 'asc', value: `${prop.name}-asc` },
            { ...prop, name: `${prop.name} - Fallande`,  direction: 'desc', value: `${prop.name}-desc` }
          ]
        })
      newSortOptions.push(...options)
      setSortOptions(newSortOptions)
    }
  }, [sortProps])

  useEffect(() => {
    const newSortedGroceries: StoreGroceryStock[] = sort(groceries, currentSortOption)
    setSortedGroceries([...newSortedGroceries])
    if (selectedGrocery && !isSaving) {
      const newSelectedGrocery: StoreGroceryStock | undefined = newSortedGroceries.find(g => g.consumerItemId === selectedGrocery.consumerItemId)
      setSelectedGrocery(newSelectedGrocery)
    }
  }, [groceries])

  function updateSorting(optionValue: string): void {
    const sortOption: GroceryFeedItemListSortOption | undefined = sortOptions!.find(option => option.value === optionValue)
    if (sortOption === undefined) {
      setSortedGroceries([...groceries])
      return
    }

    const newSortedGroceries: StoreGroceryStock[] = sort(sortedGroceries, sortOption)
    setSortedGroceries([...newSortedGroceries])	
    setCurrentSortOption(sortOption)
  }

  function sort(groceriesToSort: StoreGroceryStock[], sortOption: GroceryFeedItemListSortOption | undefined): StoreGroceryStock[] {
    if (!groceriesToSort || groceriesToSort.length === 0) {
      return []
    }
    if (sortOption === undefined) {
      return groceriesToSort
    }

    const sortModifier: number = sortOption.direction === 'asc' ? 1 : -1
    return groceriesToSort.sort((a, b) => {
      const aVal: string | number = sortOption.sortValue!(a)
      const bVal: string | number = sortOption.sortValue!(b)
      switch (typeof(aVal)) {
        case 'string':
          return aVal.localeCompare(bVal as string, 'sv') * sortModifier
        case 'number':
          return (aVal as number - (bVal as number)) * sortModifier
      }
    })
  }

  async function handleDelete(): Promise<void> {
    await deleteGrocery(selectedGrocery)
    setSelectedGrocery(undefined)
    setDeletingGrocery(false)
  }

  const computedThreshold = useMemo(() => {
    const ref = maxQuantityMode === 'custom'
      ? (maxQuantity ?? selectedGrocery?.maxQuantity ?? selectedGrocery?.restockedQuantity ?? 0)
      : (selectedGrocery?.restockedQuantity ?? 0)
    return getRunningOutThreshold(ref)
  }, [selectedGrocery, maxQuantity, maxQuantityMode])

  const thresholdTooltipText = useMemo(() => {
    const maxParam = maxQuantityMode === 'custom' ? (maxQuantity ?? selectedGrocery?.maxQuantity ?? null) : undefined
    return getRunningOutThresholdTooltip(selectedGrocery?.restockedQuantity ?? 0, selectedGrocery?.quantity ?? 0, maxParam)
  }, [selectedGrocery, maxQuantity, maxQuantityMode])


  useEffect(() => {
    if (selectedGrocery) {
      const maxQuantityMode = selectedGrocery.maxQuantity != null ? 'custom' : 'default'
      const maxQuantity = selectedGrocery.maxQuantity != null ? selectedGrocery.maxQuantity : null

      setMaxQuantityMode(maxQuantityMode)
      setMaxQuantity(maxQuantity)
      setThresholdMode(selectedGrocery.runningOutThreshold != null ? 'custom' : 'default')

      const thresholdRef = maxQuantityMode === 'custom'
        ? (maxQuantity ?? selectedGrocery.maxQuantity ?? selectedGrocery.restockedQuantity ?? 0)
        : (selectedGrocery.restockedQuantity ?? 0)

      setThresholdValue(selectedGrocery.runningOutThreshold != null ? selectedGrocery.runningOutThreshold : getRunningOutThreshold(thresholdRef))
    }
  }, [selectedGrocery])

  const handleDialogClose = async () => {
    if (!selectedGrocery) return

    if (thresholdMode === 'custom' && (thresholdValue == null || thresholdValue <= 0)) {
      setThresholdInputError('Gränsvärdet måste vara större än 0')
      return
    }
    if (maxQuantityMode === 'custom' && (maxQuantity == null || maxQuantity <= 0)) {
      setMaxQuantityInputError('Maxsaldo måste vara större än 0')
      return
    }

    const thresholdParam = thresholdMode === 'default' ? undefined : (thresholdValue ?? undefined)
    const maxQuantityParam = maxQuantityMode === 'default' ? undefined : (maxQuantity ?? undefined)

    const groceryToUpdate = selectedGrocery

    setIsSaving(true)
    setSaveError(null)
    try {
      if (onChangeStockSettings) {
        await onChangeStockSettings(groceryToUpdate, thresholdParam, maxQuantityParam)
      }
      setSelectedGrocery(undefined)
    } catch (e) {
      console.log('Error saving stock settings', e)
      setSaveError('Kunde inte spara saldoinställningar')
    } finally {
      setThresholdInputError(null)
      setMaxQuantityInputError(null)
      setIsSaving(false)
    }
  }

  return (
    <>
      {sortOptions &&
      <div className='grocery-feed-item-list__filter-wrapper mt-5'>
        <div className='grocery-feed-item-list__filter-icon pointer-events-none'>
          <ArrowIconMinimal direction='down' size='var(--grocery-feed-item-list__filter-icon-size)' color='var(--color-accent-red)'/>
        </div>
        <select className='grocery-feed-item-list__filter select-none cursor-pointer appearance-none pl-5 py-2' onChange={(e) => updateSorting(e.target.value)}>
          {sortOptions.map((sortOption, index) => (
            <option key={index} value={sortOption.value}>{sortOption.name}</option>
          ))}
        </select>
      </div>
      }
      <div className='grocery-feed-item-list__container'>
        {sortedGroceries.map((grocery, index) => {
          const stockStatus = stockText({ inStockQuantity: grocery.quantity, restockedQuantity: grocery.restockedQuantity, runningOutThreshold: grocery.runningOutThreshold })

          return (
            <div key={index} className='grocery-feed-item-list__item flex flex-row' onClick={() => setSelectedGrocery(grocery)}>
              <ItemImage ean={grocery.ean} />
              <div className='ml-3'>
                <div className='grocery-feed-item-list__item-title'>{grocery.name}</div>
                <p className='grocery-feed-item-list__item-ean'>{grocery.ean}</p>
                <div className='mt-[6px] grocery-feed-item-list__item-text'>
                  <span>Ord. pris</span>
                  <span className='grocery-feed-item-list__item-text-bold'> {displayPrice(grocery.ordinaryPrice)}</span>
                  {grocery.customPrice && 
                  <>
                    <span className='ml-2'>Lokalt pris </span>
                    <span className='grocery-feed-item-list__item-text-bold'> {displayPrice(grocery.customPrice)}</span>
                  </>
                  }
                </div>
                <div className='mt-[6px] grocery-feed-item-list__item-text'>
                  <span>Sålda</span>
                  <span className='grocery-feed-item-list__item-text-bold'> {grocery.restockedQuantity - grocery.quantity} st</span>
                  <span className='ml-2'>I skåp</span>
                  <span className='grocery-feed-item-list__item-text-bold'> {grocery.quantity} st</span>
                </div>

                {stockStatus &&
                  <div className='mt-[14px]'>
                    <Tag text={stockStatus} type={getTagTypeForStock(stockStatus) || 'secondary'} />
                  </div>
                }
                <div className='mt-[6px] flex gap-2 items-center flex-wrap'>
                  <span
                    className={`text-[12px] text-[var(--color-dark-gray)] ${
                      grocery.runningOutThreshold != null ? 'italic' : ''
                    }`}
                  >
                    {grocery.runningOutThreshold != null ? `Eget gränsvärde: ${grocery.runningOutThreshold} st` : `Gränsvärde: ${getRunningOutThreshold(grocery.maxQuantity ?? grocery.restockedQuantity ?? 0)} st`}
                  </span>
                  {grocery.maxQuantity != null && (
                    <span
                      className={'text-[12px] text-[var(--color-dark-gray)] italic'}
                    >
                      {grocery.maxQuantity != null ? `Eget maxsaldo: ${grocery.maxQuantity} st` : `Gränsvärde: ${getRunningOutThreshold(grocery.maxQuantity ?? grocery.restockedQuantity ?? 0)} st`}
                    </span>
                  )
                  }
                </div>

              </div>
              <div className='ml-auto'>
                <EditIcon size='24px' />
              </div>
            </div>
          )
        })}
      </div>   
      {selectedGrocery &&
        <IcaDialog
          open={selectedGrocery !== null}
          onClose={handleDialogClose}
          snaptotop
        >
          <IcaHeader
            slot='header'
            variant='dialog'
            heading='Hantera artikel'
            leftaction='close'
            onLeftactionclick={handleDialogClose}
          />
          <IcaCardRow noninteractive>
            <IcaMediaObject align='center'>
              <ItemImage slot='first' ean={selectedGrocery.ean} />
              <div slot='last'>
                <IcaMeta
                  heading={selectedGrocery.name}
                  subheading={selectedGrocery.ean}
                />
              </div>
            </IcaMediaObject>
          </IcaCardRow>

          <IcaSegment heading='FÖRSÄLJNING' />
          <IcaCardRow noninteractive>
            <p>Sålda sedan senast påfyllt</p>
            <p>{selectedGrocery.restockedQuantity - selectedGrocery.quantity} st</p>
          </IcaCardRow>

          <IcaSegment heading='VARUFÖRSÖRJNING' />
          <IcaCardRow noninteractive>
            <p>Antal i skåp</p>
            <p>{selectedGrocery.quantity} st</p>
          </IcaCardRow>
          <IcaCardRow noninteractive>
            <p>Senast påfyllt</p>
            <p>{dateStringToRestockDisplayDate(selectedGrocery.restockedAt)}</p>
          </IcaCardRow>

          <IcaSegment heading='MAXSALDO' />
          <div className="mt-2">
            <div
              className="custom-radio-row"
              onClick={() => { setMaxQuantityMode('default'); setMaxQuantity(null) }}
            >
              <div className="flex items-center gap-2 flex-1">
                <IcaRadio checked={maxQuantityMode === 'default'} onChange={() => { setMaxQuantityMode('default'); setMaxQuantity(null) }} />
                <IcaMeta heading={'Inget maxsaldo'} />
              </div>
            </div>
            <IcaRadio checked={maxQuantityMode === 'custom'} onChange={() => { setMaxQuantityMode('custom'); setMaxQuantity(selectedGrocery?.maxQuantity ?? 1) }} row>
              <IcaMeta heading="Ange eget maxsaldo" subheading="Högsta antal som ska finnas av artikeln i skåpet"></IcaMeta>
            </IcaRadio>
            {maxQuantityMode === 'custom' && (
              <>
                <IcaCardRow noninteractive>
                  <span>Antal</span>
                  <IcaInputNumberStepper
                    min={1}
                    value={maxQuantity ?? selectedGrocery.maxQuantity ?? 1}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setMaxQuantityInputError(null)
                      const newValue = Number(event.target.value)
                      setMaxQuantity(newValue)
                    }}
                    
                  />
                </IcaCardRow>
                {maxQuantityInputError && <p className="text-[var(--color-accent-red)] bg-[var(--color-white)] px-4 py-2">{maxQuantityInputError}</p>}
              </>
            )}
          </div>

          <IcaSegment heading='GRÄNSVÄRDE INNAN "SNART SLUT"' />
          {onChangeStockSettings && (
            <>
              <div 
                className="custom-radio-row"
              >
                <div className="flex items-center gap-2 flex-1">
                  <IcaRadio
                    checked={thresholdMode === 'default'}
                    onChange={() => {
                      setThresholdMode('default')
                      setThresholdValue(computedThreshold)
                    }}
                  />
                  <IcaMeta heading={`När det är ${computedThreshold} kvar`} subheading={maxQuantityMode === 'custom' ? `Av maxsaldo ${maxQuantity}` : `Senaste påfyllda saldot ${selectedGrocery?.restockedQuantity ?? 0}`} />
                </div>
                <div className="flex items-center">
                  <IcaTooltip position="left">
                    <IcaButtonIcon slot="trigger">
                      <tgdb-ica-icon-info style={{ fill: 'var(--color-grey-mid)', color: 'var(--color-grey-mid)' }} />
                    </IcaButtonIcon>
                    <p slot="content">{thresholdTooltipText}</p>
                  </IcaTooltip>
                </div>
              </div>
              <IcaRadio checked={thresholdMode === 'custom'} onChange={() => setThresholdMode('custom')} row>
                <IcaMeta heading="Ange eget gränsvärde" subheading='Gränsvärdet styr när artikeln visas som "Snart slut"' />
              </IcaRadio>
              {thresholdMode === 'custom' && (
                <>
                  <IcaCardRow noninteractive>
                    <span>Antal kvar</span>
                    <IcaInputNumberStepper
                      min={1}
                      value={thresholdValue ?? undefined}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setThresholdInputError(null)
                        const newValue = Number(event.target.value)
                        setThresholdValue(newValue)
                      }}
                      
                    />
                  </IcaCardRow>
                  {thresholdInputError && <p className="text-[var(--color-accent-red)] bg-[var(--color-white)] px-4 py-2">{thresholdInputError}</p>}
                </>
              )}
            </>
          )}
          
          <IcaSegment heading='PRIS' />
          <IcaCardRow noninteractive>
            <p>Ord. pris kr</p>
            <p>{displayPrice(selectedGrocery.ordinaryPrice)}</p>
          </IcaCardRow>
          <IcaCardRow noninteractive>
            <p>Lokalt pris kr</p>
            <div className='flex items-center justify-end'>
              {selectedGrocery.customPrice &&
            <div className='grocery-feed__clickable mr-4' onClick={() => deleteCustomPrice(selectedGrocery)}>
              <ResetIcon size='24px' color='var(--color-accent-red)'></ResetIcon>
            </div>}
              <TabletextInput
                className='text-end grocery-feed__local-price'
                type='number'
                placeholder={displayPrice(selectedGrocery.ordinaryPrice)}
                initialValue={displayPrice(selectedGrocery.customPrice as number) ?? undefined}
                onBlur={(newValSt) => saveCustomPrice(selectedGrocery, newValSt as number)}
                minValue={1}
                maxValue={99999}
                onInputErrorMessageChanged={(message) => setCustomPriceError(message)} 
              />
            </div>
            
          </IcaCardRow>
          {customPriceError && <InputError className='mt-2' message={customPriceError} />}
          {saveError && <InputError className='mt-2' message={saveError} />}

          <IcaButton
            slot="buttons"
            text="Klar"
            onClick={handleDialogClose}
            fullwidth
            disabled={isSaving}
          />
          <IcaButton
            slot="buttons"
            text="Ta bort artikel"
            variation="secondary"
            onClick={() => setDeletingGrocery(true)}
            fullwidth
          />
        </IcaDialog>
      }
      {selectedGrocery && deletingGrocery &&
      <IcaDialog
        open={selectedGrocery && deletingGrocery}
        onClose={() => setDeletingGrocery(false)}
        variation='action'
      >
        <IcaAttentionHeader label='Artikeln tas bort från varuskåpets sortiment' />
        <p className='pt-2 px-6 pb-6 text-[#3A3A3A]'>
          Är du säker på att du vill ta bort artikeln?
          En borttagen artikel läggs automatiskt tillbaka i sortimentet igen nästa gång du fyller på den i ett varuskåp.
        </p>
                  
        <IcaButton
          slot="buttons"
          text="Ta bort"
          onClick={handleDelete}
          fullwidth
        />
        <IcaButton
          slot="buttons"
          text="Avbryt"
          variation="secondary"
          onClick={() => setDeletingGrocery(false)}
          fullwidth
        />
      </IcaDialog>
      }
    </>
  )
}